import useTableDataDinamic from '@/hooks/useTableDataDinamic'
import { IConfigSeller } from '@/models/configSeller'
import { IUser } from '@/models/user.model'
import { webApiService } from '@/services'
import { currencyFormat } from '@/utils/currencyFormat'
import { StatusColor } from '@/utils/StatusColor.utility'
import { badgeStyle } from '@/utils/reuseBrand'
import { sbuIcon } from '@/utils/sbuIcon'
import { TradeinKpiKey } from '@/utils/tradeinStatusGroups'
import StepProgress from './StepProgress'
import { Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Chip, Select, SelectItem, Skeleton, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow } from '@nextui-org/react'
import { Button } from '@/components/ui/button'
import TradeInProgressModal from './TradeInProgressModal'
import { Input } from '@/components/ui/input'
import DoubleScrollTable from '@/components/ui/DoubleScrollTable'
import { motion } from 'framer-motion'
import { FC, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { MdOutlineSearch, MdCloudDownload, MdClose, MdDone, MdCancel } from 'react-icons/md'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import CancelTradein from '../../../TradeInPage/components/CancelTradein'
import ReceiveDevice from '../../../TradeInPage/components/ReceiveDevice'
import UploadTicket from '../../../TradeInPage/components/UploadTicket'
import { TradeinProvider } from '../../../TradeInPage/context/TradeinContext'
import CustomDateRangePicker from '../CustomDateRangePicker/CustomDateRangePicker'
import ModalTestPhonecheck from './ModalTestPhonecheck'

const columns = [
  { name: 'Trade-in', uid: 'id' },
  { name: 'Nombre', uid: 'name' },
  { name: 'Email', uid: 'email' },
  { name: 'Dispositivo', uid: 'sbu' },
  { name: 'Marca', uid: 'brand' },
  { name: 'Serie/imei', uid: 'serie' },
  { name: 'Modelo', uid: 'model' },
  { name: 'Monto Descuento', uid: 'discount_amount' },
  { name: 'Aporte', uid: 'contribution_amount' },
  { name: 'Código', uid: 'login_code' },
  { name: 'Estado', uid: 'status' },
  { name: 'Motivo', uid: 'cancel_reason' },
  { name: 'Seller', uid: 'seller' },
  { name: '', uid: 'actions' }
]

const excelName = 'Listado_TradeIn_Principal'

/* Recibir y rechazar el equipo desde el listado: las condiciones se evalúan con los campos que ya
   trae el listado (id_status, hasfiles, extra_controls_quoted), y el modal del detalle se monta solo
   al hacer click, así la carga de la página no dispara una petición por fila. */
const AccionesEquipo: FC<{ item: any, onDone: () => void }> = ({ item, onDone }) => {
  const [modal, setModal] = useState<'' | 'receive' | 'cancel'>('')
  const userState = useSelector((store: { user: IUser }) => store.user)
  const isSoloLectura = userState.id_role === Number(import.meta.env.VITE_PERFIL_SOLOLECTURA)
  const isCotizador = userState.id_role === Number(import.meta.env.VITE_PERFIL_TI_COTIZADOR)
  const idStatus = Number(item.id_status)

  // Si se cotizaron controles extra se exigen las fotos (hasfiles); si no se cotizaron (null o 0, que
  // es lo que llega de los sellers que ya no preguntan por controles), basta el estado.
  const hasFiles = Number(item.hasfiles ?? 0)
  const extraControlsQuoted = Number(item.extra_controls_quoted ?? 0)
  const canReceive = idStatus === 3 && (hasFiles === 1 || extraControlsQuoted === 0) && !isSoloLectura && !isCotizador
  // No se rechaza un tradein entregado (5), retirado (7), rechazado (8) ni en custodia (9).
  const canCancel = ![5, 7, 8, 9].includes(idStatus) && !isSoloLectura
  // El ticket de venta recién existe cuando el equipo ya fue entregado (5).
  const canUploadTicket = idStatus === 5 && !isSoloLectura && !isCotizador
  // Si el listado ya trae el ticket cargado, el botón abre el visor en vez de la cámara.
  const ticketUrl = String(item.ticket_url ?? '')

  const handleDone = () => {
    setModal('')
    onDone()
  }

  if (!canReceive && !canCancel && !canUploadTicket) return <span className='text-xs'>-</span>

  return (
    <div className='flex items-center justify-center gap-2'>
      {canReceive && (
        <Button size='sm' onClick={() => setModal('receive')}>
          <MdDone />
          Recibir equipo
        </Button>
      )}
      {canCancel && (
        <Button variant='destructive' size='sm' onClick={() => setModal('cancel')}>
          <MdCancel />
          Rechazar Tradein
        </Button>
      )}
      {canUploadTicket && (
        <UploadTicket idTradein={item.id} ticketUrl={ticketUrl} onDone={onDone} />
      )}
      {modal === 'receive' && (
        <TradeinProvider idProps={item.id} light>
          <ReceiveDevice autoOpen onClosed={() => setModal('')} onDone={handleDone} />
        </TradeinProvider>
      )}
      {modal === 'cancel' && (
        <CancelTradein autoOpen idTradein={item.id} onClosed={() => setModal('')} onDone={handleDone} />
      )}
    </div>
  )
}

interface TablePrincipalProps {
  statusIds?: number[]
  selectedKpi?: TradeinKpiKey
}

const TablePrincipal: FC<TablePrincipalProps> = ({ statusIds = [1], selectedKpi }) => {
  const [showCustomDateModal, setShowCustomDateModal] = useState(false)
  const [customStart, setCustomStart] = useState('')
  const [customEnd, setCustomEnd] = useState('')
  const [regions, setRegions] = useState<any[]>([])
  const [stores, setStores] = useState<any[]>([])
  const [selectedRegion, setSelectedRegion] = useState('')
  const [selectedStore, setSelectedStore] = useState('')
  const [toolbarEl, setToolbarEl] = useState<HTMLElement | null>(null)
  const {
    sortDescriptor,
    filterValue,
    sortedItems,
    bottomContent,
    onSearchChange,
    setSortDescriptor,
    loading,
    onClear,
    loadingExcel,
    exportToCSV,
    reload,
    setStatusFilter
  } = useTableDataDinamic(columns.concat([{ name: 'Tienda', uid: 'store' }]), excelName, 'all', statusIds, selectedRegion, selectedStore)

  const isFirstStatusRender = useRef(true)
  const statusIdsKey = statusIds.join(',')
  useEffect(() => {
    if (isFirstStatusRender.current) {
      isFirstStatusRender.current = false
      return
    }
    setStatusFilter(statusIds)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusIdsKey])
  const configSeller = useSelector((store: { config: IConfigSeller }) => store.config)
  const isTelcelCorpMexico = configSeller.config.s3.FOLDER_APP === 'ti_telcel_corp_mx'
  const isHuaweiMexico = configSeller.config.s3.FOLDER_APP === 'ti_huawei_mx'
  const isWomChile = configSeller.config.s3.FOLDER_APP === 'ti_wom'
  const isHuaweiOnlineChile = configSeller.config.s3.FOLDER_APP === 'ti_huawei_online_cl'
  const isHuaweiOnlineMexico = configSeller.config.s3.FOLDER_APP === 'ti_huawei_online_mx'
  const isHuaweiOnlinePeru = configSeller.config.s3.FOLDER_APP === 'ti_huawei_online_pe'
  const isReuseChile = configSeller.config.s3.FOLDER_APP === 'ti_reusecl'
  const isConectaPeru = configSeller.config.s3.FOLDER_APP === 'ti_conecta_peru'
  const isRipleyPeru = configSeller.config.s3.FOLDER_APP === 'ti_ripley_pe'
  const isHuaweiPeru = configSeller.config.s3.FOLDER_APP === 'ti_huawei_pe'
  const isCoppelMexico = configSeller.config.s3.FOLDER_APP === 'ti_coppel'
  const isLiverpoolMexico = configSeller.config.s3.FOLDER_APP === 'ti_liverpool_mx'
  const userState = useSelector((store: { user: IUser }) => store.user)
  const isTiRegion = userState.id_role === Number(import.meta.env.VITE_PERFIL_TI_REGION)
  const isAdmin = userState.id_role === Number(import.meta.env.VITE_PERFIL_ADMIN)
  const { slug } = useParams()

  const loadRegions = async () => {
    await webApiService.getStoreRegions(slug).then((response) => {
      setRegions(response.data ?? response ?? [])
    })
  }

  const handleRegionChange = async (region: string) => {
    setSelectedRegion(region)
    setSelectedStore('')
    setStores([])
    if (!region) return
    await webApiService.getStoresByRegion(slug, region).then((response) => {
      setStores(response.data ?? response ?? [])
    })
  }

  const loadUserRegion = async () => {
    await webApiService.getRegionByUser(userState.id).then((response) => {
      const raw = response.data ?? response
      const reg = Array.isArray(raw) ? raw[0] : raw
      const value = reg == null ? '' : String(reg.region ?? reg.name ?? reg)
      if (value) handleRegionChange(value)
    })
  }

  useEffect(() => {
    if (!isCoppelMexico) return
    loadRegions()
    if (isTiRegion) loadUserRegion()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setToolbarEl(document.getElementById('table-toolbar'))
  }, [])

  const renderCell: any = (item: any, columnKey: any) => {
    const cellValue = item[columnKey]

    /* eslint default-case: "error" */
    switch (columnKey) {
      case 'discount_amount':
        return (<span className='font-bold text-xs'>{currencyFormat(Number(cellValue), import.meta.env.VITE_COUNTRY)}</span>)
      case 'contribution_amount':
        return cellValue === null || cellValue === undefined ? '' : (<span className='font-bold text-xs'>{currencyFormat(Number(cellValue), import.meta.env.VITE_COUNTRY)}</span>)
      case 'name':
        return (
          <div className='flex flex-col leading-tight py-0.5'>
            <span className='font-bold text-xs text-slate-700'>{item.name + ' ' + item.lastname}</span>
            <span className='text-tiny text-slate-400 truncate max-w-[180px]'>{item.email}</span>
          </div>
        )
      case 'status':
        return (<Chip size='sm' radius='md' style={badgeStyle(StatusColor(cellValue, true), 0.16)}>{cellValue}</Chip>)
      case 'next_step':
        return <StepProgress item={item} />
      case 'actions':
        return (<TradeInProgressModal tradeinId={item.id} statusId={item.id_status} />)
      case 'login_code':
        return (<span className='text-xs'>{item.login_code || item.login_code_novabench || '-'}</span>)
      case 'sbu':
        return (
          <div className='flex items-start gap-1.5'>
            <span className='text-slate-400 mt-0.5'>{sbuIcon(cellValue)}</span>
            <div className='flex flex-col leading-tight'>
              <span className='text-xs font-semibold text-slate-700'>{item.brand} {item.model}</span>
              <span className='text-tiny text-slate-400'>{cellValue}</span>
            </div>
          </div>
        )
      case 'phonecheck_test':
        return item.login_code
          ? (<ModalTestPhonecheck item={item} onDone={reload} />)
          : (<span className='text-xs'>-</span>)
      case 'receive_actions':
        return (<AccionesEquipo item={item} onDone={reload} />)
      default:
        return (<span className='text-xs'>{cellValue}</span>)
    }
  }

  const handleExportCustomRange = () => {
    setShowCustomDateModal(false)
    exportToCSV('custom_range', customStart, customEnd)
    setCustomStart('')
    setCustomEnd('')
  }

  /* Email, Marca y Modelo ya se muestran integrados en las celdas "Nombre" y "Dispositivo"
     (ver renderCell), así que se ocultan como columnas propias en pantalla — pero se mantienen
     en `columns`/la exportación a Excel, que sigue trayendo cada dato en su propia columna. */
  const baseVisibleColumns = (configSeller.config.permisos.ENABLE_CONTRIBUTION !== 1)
    ? columns.filter(e => e.uid !== 'serie' && e.uid !== 'cancel_reason' && e.uid !== 'contribution_amount' && e.uid !== 'email' && e.uid !== 'brand' && e.uid !== 'model')
    : columns.filter(e => e.uid !== 'serie' && e.uid !== 'cancel_reason' && e.uid !== 'email' && e.uid !== 'brand' && e.uid !== 'model')

  /* "Progreso" es solo un hint visual en pantalla (no un campo real del tradein), así que
     se agrega únicamente a las columnas visibles de la vista "En evaluación" y nunca al array
     `columns` que también alimenta la exportación a Excel. */
  const columnsWithProgress = selectedKpi === 'evaluacion'
    ? (() => {
        const statusIndex = baseVisibleColumns.findIndex(e => e.uid === 'status')
        const withNextStep = [...baseVisibleColumns]
        withNextStep.splice(statusIndex + 1, 0, { name: 'Progreso', uid: 'next_step' })
        return withNextStep
      })()
    : baseVisibleColumns

  // La columna de prueba solo se agrega al render, no al listado que arma el excel.
  const columnsWithTest = isTelcelCorpMexico
    ? columnsWithProgress.flatMap(col => col.uid === 'login_code' ? [col, { name: 'Prueba', uid: 'phonecheck_test' }] : [col])
    : columnsWithProgress
  // Liverpool recibe y rechaza el equipo desde el listado, al lado de la columna Seller.
  const visibleColumns = isLiverpoolMexico
    ? columnsWithTest.flatMap(col => col.uid === 'seller' ? [col, { name: 'Acciones', uid: 'receive_actions' }] : [col])
    : columnsWithTest

  return (
    <motion.div
      initial={{ y: 10, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -10, opacity: 0 }}
      transition={{ duration: 0.3 }} className='space-y-4'
    >
      {toolbarEl && createPortal(
        <>
          <div className='relative'>
            <MdOutlineSearch className='pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground' />
            <Input
              value={filterValue}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder='Buscar...'
              className='w-56 pl-8 pr-7'
            />
            {filterValue && (
              <button
                type='button'
                aria-label='Limpiar búsqueda'
                onClick={() => onClear()}
                className='absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground'
              >
                <MdClose />
              </button>
            )}
          </div>
          {isCoppelMexico && (isAdmin || isTiRegion) && (
            <>
              <Select
                label='Region'
                placeholder='Region'
                selectedKeys={selectedRegion ? [selectedRegion] : []}
                onChange={(e) => handleRegionChange(e.target.value)}
                isDisabled={isTiRegion}
                className='w-[220px]'
                variant='bordered'
                classNames={{ trigger: 'bg-white' }}
              >
                {regions.map((item: any) => {
                  const value = String(item.id ?? item.region ?? item.name ?? item)
                  const labelText = String(item.name ?? item.region ?? item)
                  return (
                    <SelectItem key={value} value={value} textValue={labelText}>
                      {labelText}
                    </SelectItem>
                  )
                })}
              </Select>
              <Select
                label='Tienda'
                placeholder='Tienda'
                selectedKeys={selectedStore ? [selectedStore] : []}
                onChange={(e) => setSelectedStore(e.target.value)}
                isDisabled={!selectedRegion}
                className='w-[260px]'
                variant='bordered'
                classNames={{ trigger: 'bg-white' }}
              >
                {stores.map((item: any) => {
                  const value = String(item.id ?? item.id)
                  const labelText = `${item.seller_store_number} - ${item.store}`
                  return (
                    <SelectItem key={value} value={value} textValue={labelText}>
                      {labelText}
                    </SelectItem>
                  )
                })}
              </Select>
            </>
          )}
          <Dropdown>
            <DropdownTrigger>
              <Button variant='secondary' disabled={loadingExcel}>
                <MdCloudDownload className={loadingExcel ? 'animate-spin' : ''} />
                Descargar
              </Button>
            </DropdownTrigger>
            <DropdownMenu aria-label='Static Actions'>
              <DropdownItem key='last_week' onPress={() => exportToCSV('last_week')}>Última semana</DropdownItem>
              <DropdownItem key='last_month' onPress={() => exportToCSV('last_month')}>Últimos 30 días</DropdownItem>
              <DropdownItem key='current_month' onPress={() => exportToCSV('current_month')}>Mes actual</DropdownItem>
              <DropdownItem key='previous_month' onPress={() => exportToCSV('previous_month')}>Mes anterior</DropdownItem>
              <DropdownItem key='all' onPress={() => exportToCSV('all')}>Todo</DropdownItem>
              <DropdownItem isDisabled={isHuaweiMexico || isWomChile || isHuaweiOnlineChile || isHuaweiOnlineMexico || isHuaweiOnlinePeru || isReuseChile || isConectaPeru || isRipleyPeru || isHuaweiPeru} key='custom' onPress={() => setShowCustomDateModal(true)}>Rango Personalizado</DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </>,
        toolbarEl
      )}
      <DoubleScrollTable>
        <Table
          removeWrapper
          aria-label='Example table with client side sorting'
          sortDescriptor={sortDescriptor}
          onSortChange={setSortDescriptor}
          bottomContent={bottomContent}
          bottomContentPlacement='outside'
          classNames={{ th: 'bg-slate-100 text-slate-500 text-tiny' }}
        >
          <TableHeader columns={visibleColumns}>
            {(column) => (
              <TableColumn key={column.uid} align={column.uid === 'actions' || column.uid === 'phonecheck_test' || column.uid === 'receive_actions' ? 'center' : 'start'} allowsSorting={column.uid !== 'actions' && column.uid !== 'phonecheck_test' && column.uid !== 'receive_actions'} className={column.uid === 'nombre' || column.uid === 'actions' || column.uid === 'ordenCompraCodigo' ? '' : 'md:table-cell'}>
                {column.name}
              </TableColumn>
            )}
          </TableHeader>
          <TableBody emptyContent={loading ? '' : 'Sin resultados'}>
            {
              loading
                ? Array.from({ length: 8 }, (_, rowIndex) => (
                  <TableRow key={`skeleton-${rowIndex}`}>
                    {visibleColumns.map(column => (
                      <TableCell key={column.uid}>
                        <Skeleton className='rounded-md'><div className='h-4 w-full' /></Skeleton>
                      </TableCell>
                    ))}
                  </TableRow>
                  ))
                : sortedItems.map((item: any, rowIndex: number) => (
                  <TableRow key={item.id} className={rowIndex % 2 === 1 ? 'bg-[#f5f5f599]' : ''}>
                    {(columnKey) => <TableCell>{renderCell(item, columnKey)}</TableCell>}
                  </TableRow>
                  ))
            }
          </TableBody>
        </Table>
      </DoubleScrollTable>
      <CustomDateRangePicker
        open={showCustomDateModal}
        onClose={() => setShowCustomDateModal(false)}
        onExport={handleExportCustomRange}
        start={customStart}
        end={customEnd}
        setStart={setCustomStart}
        setEnd={setCustomEnd}
      />
    </motion.div>
  )
}

export default TablePrincipal
